// Data model types based on Amplify schema

export enum MembershipType {
  FAMILY = 'FAMILY',
  ADULT = 'ADULT',
  JUNIOR = 'JUNIOR',
  SPONSOR_GOLD = 'SPONSOR_GOLD',
  SPONSOR_SILVER = 'SPONSOR_SILVER',
  SPONSOR_BRONZE = 'SPONSOR_BRONZE',
}

export enum MemberStatus {
  PENDING = 'PENDING',
  VERIFIED = 'VERIFIED',
  REJECTED = 'REJECTED',
}

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHER = 'OTHER',
  PREFER_NOT_TO_SAY = 'PREFER_NOT_TO_SAY',
}

export enum ShirtSize {
  XS = 'XS',
  S = 'S',
  M = 'M',
  L = 'L',
  XL = 'XL',
  XXL = 'XXL',
  XXXL = 'XXXL',
}

export enum AnnouncementCategory {
  GENERAL = 'GENERAL',
  TIDE_REPORT = 'TIDE_REPORT',
  FISHING_TIPS = 'FISHING_TIPS',
  CLUB_NEWS = 'CLUB_NEWS',
  URGENT = 'URGENT',
}

export enum EventType {
  MEETING = 'MEETING',
  COMPETITION = 'COMPETITION',
  SOCIAL = 'SOCIAL',
  TRAINING = 'TRAINING',
  TRIP = 'TRIP',
}

export enum EventDifficulty {
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
  ALL_LEVELS = 'ALL_LEVELS',
}

export enum SponsorshipLevel {
  GOLD = 'GOLD',
  SILVER = 'SILVER',
  BRONZE = 'BRONZE',
}

export enum SponsorCategory {
  TACKLE_SHOP = 'TACKLE_SHOP',
  BOAT_DEALER = 'BOAT_DEALER',
  RESTAURANT = 'RESTAURANT',
  ACCOMMODATION = 'ACCOMMODATION',
  SERVICE = 'SERVICE',
  OTHER = 'OTHER',
}

export interface FamilyMember {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: Gender;
  shirtSize: ShirtSize;
  isAdult: boolean;
  memberNumber?: string;
  email?: string;
  needsPortalAccess?: boolean;
  familyGroupId?: string;
  fishingToday?: boolean; // combined comp-registration: entered in same-day competition
}

export interface Member {
  id: string;
  memberNumber?: string; // Format: TFC-XXXX
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  age?: number; // Calculated at registration
  isJunior: boolean; // Junior under the club rule (16 or under on 1 January of the membership year, shared/membership.ts); stored at registration, refreshed on 1 January
  gender: Gender;
  shirtSize: ShirtSize;
  address: string;
  suburb?: string;
  state?: string;
  postCode?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;

  // Sponsor fields
  isSponsor: boolean;
  businessName?: string;
  sponsorTier?: 'GOLD' | 'SILVER' | 'BRONZE';

  // Guardian fields (for Junior memberships)
  guardianFirstName?: string;
  guardianLastName?: string;
  guardianEmail?: string;
  guardianPhone?: string;

  // Membership details
  membershipType: MembershipType;
  familyMembers?: FamilyMember[]; // Max 5 for family membership
  isActive: boolean;
  status: MemberStatus;

  // Payment
  amountPaid: number;
  paymentReceived: boolean;

  // Acceptance tracking
  clubRulesAccepted: boolean;
  clubRulesAcceptedDate?: string;
  codeOfConductAccepted: boolean;
  codeOfConductAcceptedDate?: string;
  codeOfConductVerified: boolean;
  waiverAccepted: boolean;
  waiverAcceptedDate?: string;

  // Admin fields
  rejectionReason?: string;
  notes?: string;
  joinedDate?: string;
  membershipNumber?: string; // Legacy field
  createdAt: string;
  updatedAt: string;
}

export interface Announcement {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  publishDate: string;
  expiryDate?: string;
  isActive: boolean;
  isPinned: boolean;
  category: AnnouncementCategory;
  authorId?: string;
  authorName?: string;
  imageUrl?: string;
  tags?: string[];
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Event {
  id: string;
  title: string;
  description: string;
  shortDescription?: string;
  eventDate: string;
  location?: string;
  address?: string;
  coordinates?: string;
  rsvps?: string[];
  maxAttendees?: number;
  currentAttendees: number;
  eventType: EventType;
  isActive: boolean;
  cost?: number; // Member cost
  nonMemberPrice?: number; // Non-member cost (if different)
  allowNonMembers: boolean; // Whether non-members can attend
  currency: string;
  imageUrl?: string;
  organizerId?: string;
  organizerName?: string;
  tags?: string[];
  equipment?: string;
  difficulty: EventDifficulty;
  // Canonical catch window (competition-timing-canonical R1/R5.3) — the
  // public event card renders the fishing window from these, not from
  // free text in the title.
  competitionStartTime?: string | null;
  competitionEndTime?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Sponsor {
  id: string;
  name: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
  displayOrder: number;
  isActive: boolean;
  sponsorshipLevel: SponsorshipLevel;
  sponsorshipAmount?: number;
  sponsorshipStartDate?: string;
  sponsorshipEndDate?: string;
  benefits?: string[];
  category: SponsorCategory;
  address?: string;
  socialMedia?: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface SystemSetting {
  id: string;
  key: string;
  value: string;
  description?: string;
  category: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  submissionDate: string;
  status: string;
  adminNotes?: string;
  respondedDate?: string;
  respondedBy?: string;
  createdAt: string;
  updatedAt: string;
}

// Form types for member registration
export interface MemberRegistrationForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  age?: number;
  gender: Gender;
  shirtSize: ShirtSize;
  address: string;
  suburb?: string;
  state?: string;
  postCode?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;

  // Sponsor fields
  isSponsor: boolean;
  businessName?: string;
  sponsorTier?: 'GOLD' | 'SILVER' | 'BRONZE';

  // Guardian fields (for Junior memberships)
  guardianFirstName?: string;
  guardianLastName?: string;
  guardianEmail?: string;
  guardianPhone?: string;

  // Membership details
  membershipType: MembershipType;
  familyMembers: FamilyMember[];

  // Payment (paymentReceived set after RegistrationWizard's payment step; used by ConfirmationStep)
  paymentReceived?: boolean;

  // Combined same-day competition registration (optional)
  signupCompEventId?: string;
  signupCompFishing?: boolean; // primary's own fishing intent

  // Payment
  amountPaid?: number;

  // Acceptance tracking
  clubRulesAccepted: boolean;
  codeOfConductAccepted: boolean;
  waiverAccepted: boolean;
}

// API response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Authentication types
export interface AdminUser {
  username: string;
  email: string;
  groups: string[];
}

// UI component props
export interface LoadingState {
  isLoading: boolean;
  message?: string;
}

export interface ErrorState {
  hasError: boolean;
  message?: string;
  details?: string;
}
